import React, { useMemo } from "react";
import {
  FiUsers as Users,
  FiActivity as Activity,
  FiSettings as Settings,
  FiMessageSquare as MessageSquare,
  FiMessageCircle as MessageCircle,
} from "react-icons/fi";

import { RoomWithDetails } from "../../types";
import { RoomHeader } from "./room-header";
import { Section } from "./section";
import { ParticipantsSection } from "./sections/participants";
import { StatsSection } from "./sections/stats";
import { SettingsSection } from "./sections/settings";
import { HistorySection } from "./sections/history";
import { MessagesSection } from "./sections/messages";
import { useGetUsersByIdsQuery } from "src/features/users/services/user.Service";
import { User } from "src/features/users/types/user.entity";

type RoomInfoProps = {
  data: RoomWithDetails;
};

type UserMap = Record<string, User>;

export const RoomInfo: React.FC<RoomInfoProps> = ({ data }) => {
  const { room, participants, history, messages = [], host } = data;

  // Collect all unique user IDs from participants, history, and messages
  const userIds = useMemo(() => {
    const ids = new Set<string>();
    
    participants?.forEach((p) => ids.add(p.userId));
    history?.forEach((action) => {
      ids.add(action.userId);
      if (action.metadata?.receiverId) {
        ids.add(action.metadata.receiverId);
      }
    });
    messages?.forEach((message) => ids.add(message.userId));

    return Array.from(ids);
  }, [participants, history, messages]);

  // Fetch users using RTK Query
  const { data: usersMap = {} } = useGetUsersByIdsQuery(userIds, {
    skip: userIds.length === 0,
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Real-time indicator */}
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span>Live updates enabled</span>
        </div>

        <RoomHeader room={room} host={host} />

        <Section
          title={`Participants (${participants.length})`}
          icon={<Users className="w-5 h-5 text-blue-600" />}
          gradient="from-blue-400 to-cyan-500"
        >
          <ParticipantsSection participants={participants} hostUserId={room.userId} usersMap={usersMap} />
        </Section>

        {room.stats && (
          <Section
            title="Room Statistics"
            icon={<Activity className="w-5 h-5 text-green-600" />}
            gradient="from-green-400 to-emerald-500"
          >
            <StatsSection stats={room.stats} earnings={room.earnings} />
          </Section>
        )}

        <Section
          title="Room Settings"
          icon={<Settings className="w-5 h-5 text-purple-600" />}
          gradient="from-purple-400 to-pink-500"
        >
          <SettingsSection settings={room.settings} recording={room.recording} />
        </Section>

        <Section
          title={`Messages (${messages.length})`}
          icon={<MessageCircle className="w-5 h-5 text-teal-600" />}
          gradient="from-teal-400 to-cyan-500"
        >
          <MessagesSection messages={messages} usersMap={usersMap} />
        </Section>

        {history.length > 0 && (
          <Section
            title={`Activity History (${history.length})`}
            icon={<MessageSquare className="w-5 h-5 text-orange-600" />}
            gradient="from-orange-400 to-red-500"
          >
            <HistorySection history={history} usersMap={usersMap} />
          </Section>
        )}
      </div>
    </div>
  );
};
